import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import querystring from 'querystring';
import { Link } from 'react-router-dom';
import { List, Record } from 'immutable';
import Helmet from 'react-helmet';
import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import { DateRangePicker } from 'react-dates';
import moment from 'moment';
import _ from 'lodash';
import { startOfDay } from 'date-fns';
import classNames from 'classnames';

import { track } from '@xanadu/ui-shared/interfaces/woopra';
import { PRODUCTS } from '@xanadu/ui-shared/constants';

import { Dropdown, Option as DropdownOption } from '../../../widgets/Dropdown';

import LoadMore from '@xanadu/ui-shared/components/LoadMore';
import MarketRow from '../../MarketRow';
import SelectionRow from '../../SelectionRow';
import SelectionHeaders from '../../Headers';
import Bet from '../Bet';

import * as localise from '../../../../utils/localise';

import IconBonusBet from '../../../../../images/thirdPartyBet/icon-promos.svg';
import IconRule4DeductionFlag from '../../../../../images/thirdPartyBet/icon-R4Bet.svg';
import IconThirdPartyFlag from '../../../../../images/thirdPartyBet/icon-3rdPartyBet.svg';
import IconLiveBetting from '../../../../../images/thirdPartyBet/icon-livebetting.svg';
import IconZeroMarket from '../../../../../images/icons/icon-sport-svg/zero.svg';

import styles from './Settled.module.scss';
import '../../stylesheets/datePicker.scss';

const Event = new Record({
  id: null,
  name: '',
  sportUrl: '',
  sportName: '',
  startTime: '',
  markets: new List()
});

const PERIODS = [
  { name: 'Today', value: 'today' },
  { name: 'Last 24 Hours', value: '1-d' },
  { name: 'Last 7 Days', value: '1-w' },
  { name: 'Last Month', value: '1-M' },
  { name: 'Last 3 Months', value: '3-M' },
  { name: 'Custom...', value: 'custom' }
].map(item => ({ ...item, type: 'period', id: item.value }));

const Settled = ({
  location,
  product,
  currency,
  oddsType,
  settledBetsRequest,
  updateParams,
  sports,
  markets,
  selections,
  params,
  bets,
  total,
  netProfitAndLoss,
  error,
}) => {
  const [expandedMarkets, setExpandedMarkets] = useState([]);
  const [expandedSelections, setExpandedSelections] = useState([]);
  const [focusedInput, setFocusedInput] = useState('');

  const sportItems = [{ name: 'All', id: '' }]
    .concat(sports.toJS())
    .map(sport => ({
      ...sport,
      type: 'sportIds',
      value: sport.id
    }));

  const selectedSport = sports.find(s => s.id === params.sportIds) || { name: 'All', id: '' };
  const showDates = params.period === 'custom';
  const count = markets.toArray().length;
  const startDate = moment(params.after, 'YYYY-MM-DD');
  const endDate = moment(params.before, 'YYYY-MM-DD');
  const selectedPeriod = PERIODS.find(d => d.value === params.period).name;
  const disabled = count === 0 ? 'disabled' : '';

  const isFuturesProduct = product === PRODUCTS.futures;

  const { sportIds: paramSportIds } = params;

  let {
    before: paramBefore,
    after: paramAfter,
    period: paramPeriod
  } = params;

  const queryParameters = querystring.parse(location.search.substring(1));

  if (queryParameters.period) {
    paramPeriod = queryParameters.period;
    paramAfter = queryParameters.after;
    paramBefore = queryParameters.before;
  }

  if (paramPeriod && !['custom', 'today'].includes(paramPeriod)) {
    paramPeriod = paramPeriod.split('-');
    paramAfter = moment().subtract(paramPeriod[0], paramPeriod[1]).toISOString();
    paramBefore = null;
  } else if (paramPeriod === 'today') {
    paramAfter = startOfDay(new Date()).toISOString();
    paramBefore = null;
  } else {
    paramAfter = moment(paramAfter).toISOString();
    paramBefore = moment(paramBefore).add(1, 'days').toISOString();
  }

  const downloadParams = {
    offset: 0,
    'per-page': 1000,
    ...(Boolean(paramSportIds) && { 'sport-ids': paramSportIds }),
    ...(Boolean(paramAfter) && { after: paramAfter }),
    ...(Boolean(paramBefore) && { before: paramBefore }),
    ...(isFuturesProduct ? { products: PRODUCTS.futures } : { products: [PRODUCTS.exchange, PRODUCTS.mbzero].join(',') }),
  };

  const handleChange = (type, value) => {
    updateParams({
      [type]: value,
      offset: 0
    });
  };

  const onDropdownChange = (item) => {
    handleChange(item.type, item.value);
  };

  const handleDateChange = ({
    startDate: startDateUpdate,
    endDate: endDateUpdate
  }) => {
    let isRangeValid = false;

    if (startDateUpdate && startDateUpdate.isValid() && endDateUpdate && endDateUpdate.isValid()) {
      const diff = moment(endDateUpdate, 'YYYY-MM-DD').diff(moment(startDateUpdate, 'YYYY-MM-DD'));
      const duration = moment.duration(diff);

      if (duration.months() === 3 && duration.days() === 0) {
        isRangeValid = true;
      } else if (duration.months() < 3 && duration.days() >= 0) {
        isRangeValid = true;
      } else {
        isRangeValid = false;
      }

      if (isRangeValid) {
        if (startDateUpdate && startDateUpdate.isValid()) {
          handleChange('after', startDateUpdate.format('YYYY-MM-DD'));
        }
        if (endDateUpdate && endDateUpdate.isValid()) {
          handleChange('before', endDateUpdate.format('YYYY-MM-DD'));
        }
      } else {
        handleChange('before', startDateUpdate.add(3, 'months').format('YYYY-MM-DD'));
      }
    } else {
      if (startDateUpdate && startDateUpdate.isValid()) {
        handleChange('after', startDateUpdate.format('YYYY-MM-DD'));
      }
      if (endDateUpdate && endDateUpdate.isValid()) {
        handleChange('before', endDateUpdate.format('YYYY-MM-DD'));
      }
    }
  };

  const handleSubmit = () => {
    updateParams({
      offset: 0
    });

    settledBetsRequest();
  };

  const requestNextPage = () => {
    updateParams({
      offset: params.offset + 20
    });

    settledBetsRequest();
  };

  const toggleSelection = (id) => () => {
    const selectionsUpdate = new Set(expandedSelections);
    const action = selectionsUpdate.has(id) ? 'delete' : 'add';

    selectionsUpdate[action](id);

    setExpandedSelections(Array.from(selectionsUpdate));
  };

  const toggleMarket = (id) => () => {
    const marketsUpdate = new Set(expandedMarkets);
    const action = marketsUpdate.has(id) ? 'delete' : 'add';

    marketsUpdate[action](id);

    setExpandedMarkets(Array.from(marketsUpdate));
  };

  useEffect(() => {
    updateParams(queryParameters);
    settledBetsRequest();
  }, []);

  const netPnlClass = classNames({
    [styles.netPnl]: true,
    [styles['netPnl--positive']]: netProfitAndLoss > 0.01,
  });

  return (
    <div className={styles.main}>
      <Helmet>
        <title>
          {`Settled Bets (${[
            selectedSport ? selectedSport.name : '',
            selectedPeriod,
          ].filter(x => !!x).join(' - ')}) | Reports`}
        </title>
      </Helmet>
      {/* Filters */}
      <form className={styles.filter} onSubmit={handleSubmit}>
        <div className={styles.filterItem}>
          <label>Sport</label>
          <Dropdown
            className={styles.dropdownReports}
            value={selectedSport.name}
            theme="dark"
            items={sportItems}
            onSelect={onDropdownChange}
          >
            {
              sportItems.map(sport => (
                <DropdownOption
                  key={sport.id}
                  value={sport}
                >
                  {sport.name}
                </DropdownOption>
              ))
            }
          </Dropdown>
        </div>

        <div className={styles.filterItem}>
          <label>Period</label>
          <Dropdown
            className={styles.dropdownReports}
            value={selectedPeriod}
            items={PERIODS}
            theme="dark"
            onSelect={(item) => {
              onDropdownChange({ type: 'period', value: item.value });
            }}
          >
            {
              PERIODS.map(period => (
                <DropdownOption
                  key={period.id}
                  value={period}
                >
                  {period.name}
                </DropdownOption>
              ))
            }
          </Dropdown>
        </div>
        {showDates ? <div className={`${styles.filterItem} ${styles['filterItem--datePicker']} reportsFilterItem`}>
          <label>Custom Period - [ 3 Months Max ]</label>
          <DateRangePicker
            displayFormat="D MMM YYYY"
            numberOfMonths={1}
            startDatePlaceholderText="Start Date"
            endDatePlaceholderText="End Date"
            isOutsideRange={day => !day.isBetween(
              moment().set({ year: 2016, month: 4, date: 0 }),
              moment().add(1, 'day')
            )}
            startDate={startDate.isValid() ? startDate : undefined}
            endDate={endDate.isValid() ? endDate : undefined}
            onDatesChange={handleDateChange}
            onFocusChange={focusedInputUpdate => setFocusedInput(focusedInputUpdate)}
            focusedInput={focusedInput}
          />
        </div> : null}

        <div className={styles.filterActions}>
          <Link
            to={{
              pathname: '/edge/rest/reports/v2/bets/settled.csv',
              search: querystring.stringify(downloadParams)
            }}
            target="_blank"
            download
            rel="noopener noreferrer"
            className={`${styles.filterDownloadBtn} ${disabled}`}
            onClick={() => {
              track('Reports', 'Download CSV', {
                description: 'Settled Bets'
              });
            }}
          >
            <span className="fa fa-download" />
          </Link>

          <Link
            to={{
              ...location,
              search: querystring.stringify(
                _.omit(params.toObject(), ['offset', 'per-page'])
              )
            }}
            replace
            onClick={handleSubmit}
            rel="noopener noreferrer"
            className={styles.filterSubmitBtn}
          >
            Update Report
          </Link>
        </div>
      </form>
      {/* Filters end */}
      {error && <div className={styles.reportsError}>{error}</div>}
      <table className={styles.outerTable}>
        <thead>
          <tr>
            <td colSpan={8} className={netPnlClass}>
              Net P&L:
              <span> {localise.currency(netProfitAndLoss, currency, { symbol: true })}</span>
            </td>
          </tr>
        </thead>
        <thead>
          <tr className={styles.headers}>
            <td />
            <td className={styles.eventDetails}>
              <span className={styles.headerPrimary}>Event</span>
              <span className={styles.headerSecondary}>Start Time</span>
            </td>
            <td className={styles.marketDetails}>
              <span className={styles.headerPrimary}>Market</span>
              <span className={styles.headerSecondary}>Settled Time</span>
            </td>
            <td />
            <td className={styles.stake} />
            <td className={styles.pnl}>
              <span className={styles.headerPrimary}>Net P&amp;L</span>
              <span className={styles.headerSecondary} />
            </td>
            <td className={styles.commision}>
              <span className={styles.headerPrimary}>Comms</span>
              <span className={styles.headerSecondary}>&nbsp;</span>
            </td>
          </tr>
        </thead>
        <tbody>
          {/* NOTE: This is a reducing loop because table structure is quite strict and we can't
            nest most elements */}
          {/* TODO: Consider multiple tbodys */}
          {markets.reduce((arr, market) => {
            const event = new Event({
              id: market.eventId,
              name: market.eventName,
              startTime: market.startTime,
              sportUrl: sports.find(s => s.id === market.sportId).urlName || '',
              markets
            });

            arr.push(<MarketRow
              type="settled"
              isOpen={expandedMarkets.includes(market.id)}
              currency={currency}
              onToggle={toggleMarket(market.id)}
              count={bets.filter(bet => bet.marketId === market.id).size}
              key={market.id}
              market={market}
              event={event}
            />);

            if (!expandedMarkets.includes(market.id)) {
              return arr;
            }

            arr.push(<SelectionHeaders key={`${market.id}-headers`} type="settled" />);
            // selections
            market.selections
              .map(id => selections.find(s => id === s.id))
              .reduce((arr1, selection) => {
                arr1.push(<SelectionRow
                  currency={currency}
                  oddsType={oddsType}
                  isOpen={expandedSelections.includes(selection.id)}
                  onToggle={toggleSelection(selection.id)}
                  key={`${market.id}-${selection.id}`}
                  selection={selection}
                  bets={bets.filter(bet => bet.selectionId === selection.id)}
                  type="settled"
                />);

                if (!expandedSelections.includes(selection.id)) {
                  return arr1;
                }
                // bets-loop
                selection.bets
                  .map(id => bets.find(b => id === b.id))
                  .reduce((arr2, bet) => {
                    arr2.push(<Bet
                      key={`${market.id}-${selection.id}-${bet.id}`}
                      currency={currency}
                      oddsType={oddsType}
                      bet={bet}
                    />);

                    return arr2;
                  }, arr1);
                // end bets-loop
                return arr1;
              }, arr);
            // end selections
            return arr;
          }, [])}
          <tr>
            <td colSpan="7">
              <div className={styles.containerLegend}>
                <div className={styles.legend}>
                  <div className={styles.legendItem}>
                    <div className={`${styles.legendIcon} ${styles['legendIcon--inPlay']}`}>
                      <IconBonusBet />
                    </div>
                    <span className={styles.legendText}>Bet was placed using a free bet</span>
                  </div>
                  <div className={styles.legendItem}>
                    <div className={`${styles.legendIcon} ${styles['legendIcon--inPlay']}`}>
                      <IconLiveBetting />
                    </div>
                    <span className={styles.legendText}>Bet was placed In-Play</span>
                  </div>
                  <div className={styles.legendItem}>
                    <div className={`${styles.legendIcon} ${styles['legendIcon--inPlay']}`}>
                      <IconThirdPartyFlag />
                    </div>
                    <span className={styles.legendText}>
                      Bet was placed via a 3rd party integration
                    </span>
                  </div>
                  <div className={styles.legendItem}>
                    <div className={`${styles.legendIcon} ${styles['legendIcon--inPlay']}`}>
                      <IconRule4DeductionFlag />
                    </div>
                    <span className={styles.legendText}>Bet includes a Rule 4 deduction&nbsp;
                      <Link
                        to="/page/rules_and_regulations/sports-rules/#heading-horse-racing"
                        className={styles.rule4Deduction}
                      > (explanation)</Link>
                    </span>
                  </div>
                  <div className={styles.legendItem}>
                    <div className={`${styles.legendIcon} ${styles['legendIcon--inPlay--Zero']}`}>
                      <IconZeroMarket />
                    </div>
                    <span className={styles.legendText}>Bet was placed on Matchbook Zero</span>
                  </div>
                </div>
                <div className={styles.legend}>
                  <LoadMore
                    inline
                    total={total}
                    count={markets.size}
                    itemName="Markets"
                    onClick={requestNextPage}
                  />
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

Settled.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string.isRequired
  }).isRequired,
  currency: PropTypes.string,
  product: PropTypes.string,
  oddsType: PropTypes.string,
  settledBetsRequest: PropTypes.func,
  updateParams: PropTypes.func.isRequired,
  sports: PropTypes.instanceOf(List),
  markets: PropTypes.instanceOf(List),
  selections: PropTypes.instanceOf(List),
  params: PropTypes.instanceOf(Record).isRequired,
  bets: PropTypes.instanceOf(List),
  total: PropTypes.number,
  netProfitAndLoss: PropTypes.number,
  error: PropTypes.string
};

export default Settled;
